/**
 * MOD writer.
 *
 * MOD is a much narrower format than the editor's internal model, so saving is
 * lossy in ways worth naming out loud: one sample per instrument, 8-bit only,
 * 64 rows per pattern, notes confined to ProTracker's three octaves. The writer
 * reports every compromise it makes rather than silently mangling the song.
 */
import {
  CELL_EFFECT,
  CELL_INSTRUMENT,
  CELL_NOTE,
  CELL_PARAM,
  CELL_STRIDE,
  CELL_VOLUME,
  LOOP_NONE,
  type Song,
} from '../types';
import { MAX_NOTE, MOD_NOTE_OFFSET, PT_PERIOD_TABLE } from '../constants';
import { ByteWriter } from '../loaders/binary';

export interface SaveResult {
  data: Uint8Array;
  warnings: string[];
}

/** Channel counts that have a standard four-character tag. */
function magicFor(channels: number): string {
  if (channels === 4) return 'M.K.';
  if (channels < 10) return `${channels}CHN`;
  return `${channels}CH`;
}

export function saveMod(song: Song): SaveResult {
  const warnings: string[] = [];
  const w = new ByteWriter(1 << 20);
  const channels = song.channels;

  if (channels > 32) {
    throw new Error(`MOD supports at most 32 channels; this song has ${channels}. Save as XM instead.`);
  }

  w.str(song.name.slice(0, 20), 20);

  // --- 31 sample headers --------------------------------------------------
  const sampleData: Uint8Array[] = [];
  for (let i = 0; i < 31; i++) {
    const inst = song.instruments[i];
    const smp = inst?.samples[0];

    if (inst && inst.samples.length > 1) {
      warnings.push(`Instrument ${i + 1} has ${inst.samples.length} samples; only the first is kept.`);
    }

    if (!smp || smp.length === 0) {
      w.str('', 22);
      w.u16be(0);
      w.u8(0);
      w.u8(0);
      w.u16be(0);
      w.u16be(1);
      sampleData.push(new Uint8Array(0));
      continue;
    }

    // MOD stores lengths in words, so an odd frame count loses its last frame.
    let length = smp.length;
    if (length > 131070) {
      warnings.push(`Sample "${smp.name}" truncated to the 128 KB MOD limit.`);
      length = 131070;
    }
    const words = length >> 1;

    const bytes = new Uint8Array(words * 2);
    for (let n = 0; n < words * 2; n++) {
      const v = Math.max(-1, Math.min(1, smp.data[n] ?? 0));
      bytes[n] = Math.round(v * 127) & 0xff;
    }
    if (smp.bits === 16) {
      warnings.push(`Sample "${smp.name}" downsampled from 16-bit to 8-bit.`);
    }
    sampleData.push(bytes);

    // MOD finetune is ±8 in eighth-semitone steps; XM's ±128 divides by 16.
    const finetune = Math.max(-8, Math.min(7, Math.round(smp.finetune / 16)));
    if (smp.relativeNote !== 0) {
      warnings.push(`Sample "${smp.name}" has a relative note offset, which MOD cannot store.`);
    }

    w.str(smp.name.slice(0, 22), 22);
    w.u16be(words);
    w.u8(finetune & 0x0f);
    w.u8(Math.max(0, Math.min(64, smp.volume)));

    if (smp.loopType !== LOOP_NONE && smp.loopLength > 2) {
      w.u16be(Math.min(words, smp.loopStart >> 1));
      w.u16be(Math.min(words, smp.loopLength >> 1));
    } else {
      w.u16be(0);
      w.u16be(1); // ProTracker's idiom for "no loop"
    }
  }

  // --- Order table --------------------------------------------------------
  const songLength = Math.min(128, song.songLength);
  if (song.songLength > 128) warnings.push('Order list truncated to 128 entries.');
  w.u8(songLength);
  w.u8(song.restartPosition < 128 ? song.restartPosition : 127);
  for (let i = 0; i < 128; i++) w.u8(song.order[i] ?? 0);
  w.str(magicFor(channels), 4);

  // --- Patterns -----------------------------------------------------------
  let patternCount = 0;
  for (let i = 0; i < songLength; i++) patternCount = Math.max(patternCount, song.order[i] ?? 0);
  patternCount += 1;

  let noteRangeWarned = false;
  let effectWarned = false;
  let volumeColumnWarned = false;

  for (let p = 0; p < patternCount; p++) {
    const pattern = song.patterns[p];
    if (!pattern) {
      for (let i = 0; i < 64 * channels * 4; i++) w.u8(0);
      continue;
    }
    if (pattern.rows !== 64 && p === 0) {
      warnings.push(`Patterns are ${pattern.rows} rows; MOD requires 64, so they are padded or clipped.`);
    }

    for (let row = 0; row < 64; row++) {
      for (let ch = 0; ch < channels; ch++) {
        if (row >= pattern.rows) {
          w.u8(0);
          w.u8(0);
          w.u8(0);
          w.u8(0);
          continue;
        }
        const off = (row * channels + ch) * CELL_STRIDE;
        const note = pattern.data[off + CELL_NOTE];
        const instrument = pattern.data[off + CELL_INSTRUMENT];
        const volume = pattern.data[off + CELL_VOLUME];
        let effect = pattern.data[off + CELL_EFFECT];
        let param = pattern.data[off + CELL_PARAM];

        let period = 0;
        if (note > 0 && note <= MAX_NOTE) {
          const index = note - 1 - MOD_NOTE_OFFSET;
          if (index >= 0 && index < 36) {
            period = PT_PERIOD_TABLE[0][index];
          } else if (!noteRangeWarned) {
            warnings.push('Some notes fall outside ProTracker\'s three octaves and were dropped.');
            noteRangeWarned = true;
          }
        }

        if (volume > 0 && !volumeColumnWarned) {
          warnings.push('Volume-column data cannot be stored in MOD and was dropped.');
          volumeColumnWarned = true;
        }
        if (effect > 0x0f) {
          if (!effectWarned) {
            warnings.push('XM-only effects (G/H/K/L/P/R/T/X) cannot be stored in MOD and were dropped.');
            effectWarned = true;
          }
          effect = 0;
          param = 0;
        }

        const inst = instrument & 0x1f;
        w.u8((inst & 0xf0) | ((period >> 8) & 0x0f));
        w.u8(period & 0xff);
        w.u8(((inst & 0x0f) << 4) | (effect & 0x0f));
        w.u8(param & 0xff);
      }
    }
  }

  for (const bytes of sampleData) w.raw(bytes);

  return { data: w.finish(), warnings };
}
